import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function CourseCategoriesSection() {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/course-categories")
            .then((r) => setCats(r.data || []))
            .catch(() => setCats([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section id="courses" className="py-20 md:py-28 bg-paper" data-testid="course-categories-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold tracking-wide uppercase">
                            Our Courses
                        </span>
                        <h2 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900">
                            Find your path
                        </h2>
                        <p className="mt-4 text-lg text-ink-700">
                            Browse by category — pick the lineage that calls to you and we'll show you every course inside.
                        </p>
                    </div>
                    <a
                        href="#contact"
                        className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700"
                        data-testid="course-categories-view-all"
                    >
                        Talk to a teacher <ArrowRight className="h-4 w-4" />
                    </a>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="h-80 rounded-2xl bg-gray-100 animate-pulse" />
                        ))}
                    </div>
                ) : cats.length === 0 ? (
                    <p className="text-center py-10 text-ink-700">No course categories available yet — check back soon.</p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="course-category-grid">
                        {cats.map((c, idx) => (
                            <Link
                                key={c.id}
                                to={`/course-categories/${c.slug}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-1 animate-fade-up"
                                style={{ animationDelay: `${idx * 70}ms` }}
                                data-testid={`course-category-card-${c.id}`}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                                    {c.feature_image ? (
                                        <img
                                            src={c.feature_image}
                                            alt={c.name}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-ink-400">
                                            <span className="font-heading text-3xl">अ</span>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                                </div>
                                <div className="p-6">
                                    <h3 className="font-heading text-xl md:text-2xl font-semibold text-ink-900 group-hover:text-brand-600 transition-colors">
                                        {c.name}
                                    </h3>
                                    {c.description && (
                                        <p className="mt-2 text-sm text-ink-700 leading-relaxed line-clamp-2">
                                            {c.description}
                                        </p>
                                    )}
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">
                                        Explore courses <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
